/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useApiKeyForm.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing API Key form state and input changes.
────────────────────────────────────────────────────────────────*/

import { useState, useMemo } from 'react';
import { ApiKeyFormData } from '../types';

export interface ApiKeyFormState {
  name: string;
  description: string;
  tenantId: string | number | null;
  domainId: number | null; // null = all domains, specific id = single domain
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  permissions: any;
  isActive: boolean;
  expiresAt: string;
}

const initialFormState: ApiKeyFormState = {
  name: '',
  description: '',
  tenantId: null,
  domainId: null,
  permissions: {},
  isActive: true,
  expiresAt: '',
};

export function useApiKeyForm() {
  const [formData, setFormData] = useState<ApiKeyFormState>(initialFormState);
  const [originalData, setOriginalData] = useState<ApiKeyFormState>(initialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const resetForm = (newData?: ApiKeyFormState) => {
    const data = newData || initialFormState;
    setFormData(data);
    setOriginalData(data);
    setErrors({});
  };

  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  return {
    formData,
    originalData,
    errors,
    setFormData,
    setOriginalData,
    setErrors,
    handleInputChange,
    resetForm,
    hasChanges,
  };
}

